import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Puede la depresión volverse resistente a la medicación?",
    answer:
      "La depresión resistente al tratamiento se define como la falta de respuesta a al menos dos antidepresivos en dosis y duración adecuadas. Afecta a aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor. Es común y tratable con opciones como TMS, SAINT® y SPRAVATO®.",
  },
  {
    question: "¿Qué hago si mi depresión ya no responde a la medicación?",
    answer:
      "Hable con su médico. Las opciones incluyen ajustar la dosis, cambiar de medicación, añadir aumento o considerar tratamientos sin medicación como TMS, SAINT® o SPRAVATO®. Una evaluación psiquiátrica puede identificar la causa y recomendar los siguientes pasos.",
  },
  {
    question: "¿La TMS funciona para la depresión resistente a la medicación?",
    answer:
      "Sí. La TMS está autorizada por la FDA específicamente para adultos cuya depresión no ha mejorado con antidepresivos. SAINT® es un protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177).",
  },
];

export const esDepressionResistantToMedication = makeEsArticle({
  slug: "can-depression-become-resistant-to-medication",
  title:
    "¿Puede la depresión volverse resistente a la medicación? | Neuro Wellness",
  description:
    "La depresión resistente al tratamiento: definición, por qué ocurre y opciones como TMS, SAINT® y SPRAVATO® cuando los antidepresivos no funcionan.",
  h1: "¿Puede la depresión volverse resistente a la medicación?",
  breadcrumbLabel: "Depresión resistente a la medicación",
  shortAnswer:
    "La depresión resistente al tratamiento se define como la falta de respuesta a al menos dos antidepresivos en dosis y duración adecuadas. Afecta a aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor. No es que la medicación 'deje de funcionar', sino que la depresión de algunas personas no responde a los tratamientos estándar. Las opciones incluyen ajustes de medicación, TMS, SAINT® TMS acelerada y SPRAVATO®. Una evaluación psiquiátrica puede identificar la causa y recomendar los siguientes pasos.",
  keyTakeaways: [
    "La depresión resistente al tratamiento = falta de respuesta a ≥2 antidepresivos.",
    "Afecta a ~1/3 de las personas tratadas por trastorno depresivo mayor.",
    "No significa que la medicación 'deje de funcionar'.",
    "TMS, SAINT® y SPRAVATO® son opciones más allá de la medicación.",
    "Una evaluación psiquiátrica identifica los siguientes pasos.",
  ],
  sections: [
    {
      heading: "Qué es la depresión resistente a la medicación",
      paragraphs: [
        "La depresión resistente al tratamiento se define como la falta de respuesta a al menos dos antidepresivos probados en dosis y duración adecuadas. Aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor no alcanzan la remisión tras dos pasos de tratamiento, según el estudio STAR*D.",
        "No es que la medicación 'deje de funcionar' con el tiempo. Más bien, la depresión de algunas personas no responde a los tratamientos estándar desde el principio o tras varios ensayos.",
      ],
    },
    {
      heading: "Por qué ocurre",
      paragraphs: [
        "Varios factores contribuyen: dosis o duración inadecuadas, medicamento no adecuado para el cerebro de la persona, error diagnóstico (como confundir depresión bipolar con unipolar), condiciones comórbidas o una depresión genuinamente resistente al tratamiento.",
      ],
    },
    {
      heading: "Qué hacer",
      paragraphs: [
        "Hable con su médico. Las opciones incluyen ajustar la dosis, cambiar de medicación, añadir aumento o considerar tratamientos sin medicación. La TMS está autorizada por la FDA para la depresión que no ha respondido a medicación. SAINT® es un protocolo acelerado de cinco días autorizado por la FDA (K220177). SPRAVATO® está aprobado por la FDA para la depresión resistente al tratamiento.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
